import React, { useState, useEffect } from "react";
import { Tooltip, IconButton, HStack, Select, useToast } from "@chakra-ui/react";
import { HiOutlinePlus } from "react-icons/hi";
import { HandleInsertHeading } from "../utils/HandleInsertHeading"; // named export
import { unwrapFetchResponse } from "../utils/unwrapApiResponse";
import { useAuth } from "../AuthContext";

const CODIFY_API = "https://codify.poc.mrc.ai/api/definitions";
const DOC_DEFS_API = "https://documents.poc.mrc.ai/api/definitions";

export default function HeadingInserter({ editor, codifyCollection, documentDefinitionId }) {
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(false);
  const [documentDefinition, setDocumentDefinition] = useState(null);
  const toast = useToast();
  const { user } = useAuth();
  const idToken = user?.idToken;

  // Fetch document definition when documentDefinitionId changes
  useEffect(() => {
    const fetchDocumentDefinition = async () => {
      if (!documentDefinitionId) {
        setDocumentDefinition(null);
        return;
      }

      try {
        const response = await fetch(`${DOC_DEFS_API}/${documentDefinitionId}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch document definition: ${response.status}`);
        }
        const definition = await unwrapFetchResponse(response);
        setDocumentDefinition(definition);
      } catch (error) {
        console.error("Error fetching document definition:", error);
        // Don't show toast for this - it's not critical
        setDocumentDefinition(null);
      }
    };

    fetchDocumentDefinition();
  }, [documentDefinitionId, idToken]);

  // Fetch types when codifyCollection changes
  useEffect(() => {
    const fetchTypes = async () => {
      if (!codifyCollection) {
        setTypes([]);
        setSelectedType("");
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${CODIFY_API}?collection=${codifyCollection}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch types: ${response.status}`);
        }
        const definitions = await response.json();

        // Extract unique types from definitions
        const uniqueTypes = [...new Set(definitions.map(d => d.type))]
          .filter(Boolean)
          .sort();

        setTypes(uniqueTypes);
        setSelectedType(""); // Reset selection when types change
      } catch (error) {
        console.error("Error fetching types for collection:", error);
        toast({
          title: "Error loading types",
          description: error.message || "Failed to fetch types from Codify API",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
  }, [codifyCollection, idToken, toast]);

  // Helper function to find metadata for a given CodifyDefinition type
  // This searches DocumentDefinition using the Type field from CodifyDefinition
  // and returns the corresponding heading, headingType, value, editableBy, and adminBy
  const findHeadingMetadata = (codifyType) => {
    if (!documentDefinition?.segments) {
      return { heading: codifyType, headingType: codifyType, value: "", editableBy: [], adminBy: [] };
    }

    // Search through all segments and their content items
    // Look for a content item where the headingType matches the codifyType
    for (const segment of documentDefinition.segments) {
      if (!segment.content) continue;

      for (const contentItem of segment.content) {
        // Match by headingType field against the CodifyDefinition type
        if (contentItem.headingType === codifyType) {
          return {
            heading: contentItem.heading || codifyType, // Use heading field from DocumentDefinition
            headingType: contentItem.headingType || codifyType,
            value: contentItem.value || "", // Include default value (can be rich text JSON)
            editableBy: Array.isArray(contentItem.editableBy) ? contentItem.editableBy : [],
            adminBy: Array.isArray(contentItem.adminBy) ? contentItem.adminBy : [],
          };
        }
      }
    }

    // Default if not found - use codifyType as fallback
    return { heading: codifyType, headingType: codifyType, value: "", editableBy: [], adminBy: [] };
  };

  const onInsertClick = () => {
    if (!selectedType) return;

    // Get heading, headingType, value, editableBy and adminBy from the document definition
    // selectedType is the Type from CodifyDefinition
    const { heading, headingType, value, editableBy, adminBy } = findHeadingMetadata(selectedType);

    HandleInsertHeading(editor, {
      fieldId: headingType.toLowerCase().replace(/\s+/g, "_"),
      headingType: headingType, // Use headingType from DocumentDefinition
      headingText: String(heading || "HEADING").toUpperCase(), // Use heading field from DocumentDefinition
      contentText: value || "None", // Use the default value from definition (can be rich text JSON or plain text)
      fontSize: "12pt",
      editableBy: editableBy.length > 0 ? editableBy : ["insurer"], // Fallback to ["insurer"] if not found
      adminBy: adminBy.length > 0 ? adminBy : [],
    });
  };

  return (
    <HStack alignItems="center" justifyContent="center" width="100%">
      <Select
        placeholder={loading ? "Loading types..." : codifyCollection ? "Select type..." : "No collection set"}
        value={selectedType}
        onChange={(e) => setSelectedType(e.target.value)}
        size="xs"
        height="32px"
        fontSize="xs"
        isDisabled={!codifyCollection || loading || types.length === 0}
        flex="1"
      >
        {types.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </Select>

      <Tooltip label="Insert Heading...">
        <IconButton
          onClick={onInsertClick}
          icon={<HiOutlinePlus />}
          variant="outline"
          size="sm"
          bg="white"
          border="1px solid"
          borderColor="gray.300"
          _hover={{ bg: "gray.100" }}
          minW="2.5rem"
          isDisabled={!selectedType || !codifyCollection}
          alignSelf="flex-start"
        />
      </Tooltip>
    </HStack>
  );
}
